import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Send, Check, CheckCheck, Paperclip } from "lucide-react";
import { useUser } from "../context/UserContext";
import { listConversations, listMessages, markConversationRead, uploadChatAttachment, formatApiError } from "../lib/api";
import { timeAgo } from "../lib/constants";
import DangerousFileLink, { DANGEROUS_EXTS, isDangerousUrl } from "./DangerousFileLink";
import VerifiedBadge from "./VerifiedBadge";
import { toast } from "sonner";

const URL_REGEX = /(https?:\/\/[^\s<>"']+|\/api\/uploads\/files\/[^\s<>"']+)/gi;

const renderMessageContent = (text, verified) => {
  if (!text) return null;
  const parts = [];
  let lastIndex = 0;
  let m;
  URL_REGEX.lastIndex = 0;
  while ((m = URL_REGEX.exec(text)) !== null) {
    if (m.index > lastIndex) parts.push({ type: "text", value: text.slice(lastIndex, m.index) });
    parts.push({ type: "url", value: m[0] });
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) parts.push({ type: "text", value: text.slice(lastIndex) });

  return parts.map((p, i) => {
    if (p.type === "text") return <span key={i}>{p.value}</span>;
    if (isDangerousUrl(p.value)) {
      return (
        <span key={i} className="block my-1.5">
          <DangerousFileLink url={p.value} verified={verified} testId={`chat-dangerous-${i}`} fullWidth />
        </span>
      );
    }
    return (
      <a key={i} href={p.value} target="_blank" rel="noopener noreferrer" className="text-[#00ff88] underline break-all">
        {p.value}
      </a>
    );
  });
};

const TYPING_TTL_MS = 3500;

export const ChatPanel = () => {
  const {
    user, chatOpen, closeChat,
    activeConversation, setActiveConversation,
    chatSocket, wsEvent,
  } = useUser();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [typingPeers, setTypingPeers] = useState({}); // { [conversationId]: timestampUntil }
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const lastTypingSentRef = useRef(0);
  const myProfileId = user?.id;

  // Initial conversations load whenever the panel opens.
  useEffect(() => {
    if (!chatOpen) return;
    listConversations()
      .then((res) => {
        setConversations(res);
        if (!activeConversation && res.length > 0) setActiveConversation(res[0]);
      })
      .catch(() => setConversations([]));
  }, [chatOpen, user?.id]);

  // Initial messages on conversation change.
  useEffect(() => {
    if (!chatOpen || !activeConversation) { setMessages([]); return; }
    let cancelled = false;
    listMessages(activeConversation.id)
      .then((res) => { if (!cancelled) setMessages(res); })
      .catch(() => {});
    // Tell the server we've opened this thread → clears unread badge.
    markConversationRead(activeConversation.id).catch(() => {});
    chatSocket?.send({ type: "read", conversation_id: activeConversation.id });
    return () => { cancelled = true; };
  }, [chatOpen, activeConversation?.id]);

  // React to incoming WebSocket events.
  useEffect(() => {
    if (!wsEvent) return;
    const ev = wsEvent;
    if (ev.type === "message") {
      // Append to current thread if it matches; otherwise refresh conv list.
      if (ev.conversation_id === activeConversation?.id) {
        setMessages((prev) => {
          // Replace optimistic message if its content matches.
          const idx = prev.findIndex((m) => m._pending && m.content === ev.message.content);
          if (idx >= 0) {
            const copy = prev.slice();
            copy[idx] = ev.message;
            return copy;
          }
          if (prev.some((m) => m.id === ev.message.id)) return prev;
          return [...prev, ev.message];
        });
        // If chat is open + this is the active thread, auto-mark as read.
        if (chatOpen && ev.message.sender_id !== myProfileId) {
          chatSocket?.send({ type: "read", conversation_id: ev.conversation_id });
        }
      }
      // Update conversation list preview.
      setConversations((prev) => {
        const found = prev.find((c) => c.id === ev.conversation_id);
        if (!found) {
          // New conversation — fetch fresh.
          listConversations().then(setConversations).catch(() => {});
          return prev;
        }
        return prev.map((c) =>
          c.id === ev.conversation_id
            ? { ...c, last_message: ev.message.content, last_message_at: ev.message.created_at }
            : c,
        );
      });
    } else if (ev.type === "read" && ev.conversation_id === activeConversation?.id) {
      // Other side read our messages — flip local read flag.
      setMessages((prev) => prev.map((m) => (m.sender_id === myProfileId ? { ...m, read: true, read_at: ev.at } : m)));
    } else if (ev.type === "typing") {
      if (ev.is_typing && ev.conversation_id) {
        setTypingPeers((p) => ({ ...p, [ev.conversation_id]: Date.now() + TYPING_TTL_MS }));
      } else {
        setTypingPeers((p) => {
          const copy = { ...p };
          delete copy[ev.conversation_id];
          return copy;
        });
      }
    }
  }, [wsEvent]);

  // Garbage-collect stale "is typing" markers.
  useEffect(() => {
    const t = setInterval(() => {
      setTypingPeers((p) => {
        const now = Date.now();
        const next = {};
        for (const [k, v] of Object.entries(p)) if (v > now) next[k] = v;
        return next;
      });
    }, 1000);
    return () => clearInterval(t);
  }, []);

  // Auto-scroll to the bottom whenever messages change.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, typingPeers[activeConversation?.id]]);

  const send = (overrideText) => {
    const raw = typeof overrideText === "string" ? overrideText : input;
    if (!raw.trim() || !activeConversation) return;
    const text = raw.trim();
    if (typeof overrideText !== "string") setInput("");
    // Optimistic append — the WS broadcast will replace it once the server echoes.
    const optimistic = {
      id: `tmp-${Date.now()}`,
      conversation_id: activeConversation.id,
      sender_id: myProfileId,
      sender_role: user.role,
      content: text,
      created_at: new Date().toISOString(),
      read: false,
      _pending: true,
    };
    setMessages((prev) => [...prev, optimistic]);
    chatSocket?.send({ type: "send", conversation_id: activeConversation.id, content: text });
    // Stop typing indicator on send.
    chatSocket?.send({ type: "typing", conversation_id: activeConversation.id, is_typing: false });
  };

  const onPickFile = () => {
    if (uploading || !activeConversation) return;
    fileInputRef.current?.click();
  };

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = ""; // allow re-selecting same file
    if (!file || !activeConversation) return;
    setUploading(true);
    try {
      const res = await uploadChatAttachment(file);
      const url = res?.url;
      if (!url) throw new Error("Upload failed");
      // Send as a normal message — DangerousFileLink picks up the URL.
      send(url);
    } catch (err) {
      toast.error(formatApiError(err, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const onInputChange = (e) => {
    setInput(e.target.value);
    if (!activeConversation) return;
    const now = Date.now();
    if (now - lastTypingSentRef.current > 1500) {
      chatSocket?.send({ type: "typing", conversation_id: activeConversation.id, is_typing: true });
      lastTypingSentRef.current = now;
    }
  };

  const peerTyping = useMemo(() => {
    if (!activeConversation) return false;
    return (typingPeers[activeConversation.id] || 0) > Date.now();
  }, [typingPeers, activeConversation?.id]);

  if (!chatOpen) return null;

  const otherName = (c) => (user?.role === "company" ? c.creator_name : c.company_name);
  // Only show the creator-verified badge to companies (peer = creator).
  const peerVerifiedFlag = (c) => (user?.role === "company" ? !!c?.creator_verified : false);

  return (
    <>
      <div
        data-testid="chat-overlay"
        onClick={closeChat}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
      />
      <aside
        data-testid="chat-panel"
        className="fixed top-0 right-0 bottom-0 w-full max-w-[520px] z-50 transition-transform duration-300 ease-out translate-x-0"
      >
        <div className="h-full flex bg-[#06060c] border-l border-[#15152a]">
          <div className="w-44 md:w-56 border-r border-[#15152a] flex flex-col">
            <div className="px-3 py-3 border-b border-[#15152a] flex items-center justify-between">
              <div>
                <div className="cc-overline">Messages</div>
                <div className="text-sm text-white">{conversations.length} threads</div>
              </div>
              <button
                data-testid="chat-close-btn"
                onClick={closeChat}
                className="p-1.5 rounded-md text-[#a0a0b0] hover:text-white hover:bg-[#0a0a14]"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {conversations.length === 0 && (
                <div className="px-3 py-6 text-xs text-[#606070]">No conversations yet.</div>
              )}
              {conversations.map((c) => {
                const myUnread = (user?.role === "company" ? c.unread_company : c.unread_creator) || 0;
                return (
                  <button
                    key={c.id}
                    data-testid={`conv-item-${c.id}`}
                    onClick={() => setActiveConversation(c)}
                    className={`w-full text-left px-3 py-3 border-b border-[#15152a] hover:bg-[#0a0a14] transition-colors ${activeConversation?.id === c.id ? "bg-[#0a0a14]" : ""}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <span className="w-7 h-7 rounded-full bg-[#0d0d1a] border border-[#15152a] flex items-center justify-center text-[#00ff88] text-sm">
                          {user?.role === "company" ? c.creator_glyph || "◈" : "◆"}
                        </span>
                        {c.other_online && (
                          <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#00ff88] border-2 border-[#06060c]" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-white truncate flex items-center gap-1.5">
                          <span className="truncate">{otherName(c)}</span>
                          <VerifiedBadge verified={peerVerifiedFlag(c)} testIdSuffix={`conv-${c.id}`} size="xs" showInfo={false} />
                          {myUnread > 0 && (
                            <span className="ml-auto bg-[#00ff88] text-[#06060c] rounded-full px-1.5 text-[10px] font-bold">
                              {myUnread > 9 ? "9+" : myUnread}
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-[#606070] truncate font-mono">{timeAgo(c.last_message_at)}</div>
                      </div>
                    </div>
                    <div className="text-[11px] text-[#a0a0b0] truncate mt-1">{c.last_message || "—"}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex-1 flex flex-col">
            {activeConversation ? (
              <>
                <div className="px-5 py-3 border-b border-[#15152a] flex items-center gap-3">
                  <div className="relative">
                    <div className="w-9 h-9 rounded-full bg-[#0d0d1a] border border-[#15152a] flex items-center justify-center text-[#00ff88]">
                      {user?.role === "company" ? activeConversation.creator_glyph || "◈" : "◆"}
                    </div>
                    {activeConversation.other_online && (
                      <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#00ff88] border-2 border-[#06060c]" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm text-white flex items-center gap-1.5">
                      <span>{otherName(activeConversation)}</span>
                      <VerifiedBadge verified={peerVerifiedFlag(activeConversation)} testIdSuffix="chat-header" size="sm" />
                    </div>
                    <div className="text-[10px] text-[#606070] uppercase tracking-widest">
                      {activeConversation.other_online ? "Online" : (user?.role === "company" ? "Creator" : "Company")}
                    </div>
                  </div>
                </div>

                <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-6 space-y-3">
                  {messages.map((m) => {
                    const mine = m.sender_id === myProfileId;
                    // Trust URLs less when the OTHER side (peer) is sending them — show
                    // amber for verified company senders, red otherwise. The peer's
                    // verified status is on the conversation only when they're a company.
                    const peerVerified = !mine && user?.role === "creator"
                      ? !!activeConversation?.company_verified
                      : false;
                    return (
                      <div
                        key={m.id}
                        data-testid={`msg-${m.id}`}
                        className={`flex ${mine ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-2xl p-3 text-sm ${
                            mine
                              ? "bg-[#00ff88]/10 border border-[#00ff88]/25 text-white rounded-tr-sm"
                              : "bg-[#0a0a14] border border-[#15152a] text-white rounded-tl-sm"
                          } ${m._pending ? "opacity-60" : ""}`}
                        >
                          <div className="whitespace-pre-wrap break-words">
                            {renderMessageContent(m.content, peerVerified)}
                          </div>
                          <div className="text-[10px] text-[#606070] mt-1 flex items-center gap-1 justify-end">
                            <span>{timeAgo(m.created_at)}</span>
                            {mine && !m._pending && (
                              m.read ? (
                                <CheckCheck size={11} className="text-[#00ff88]" data-testid={`msg-read-${m.id}`} />
                              ) : (
                                <Check size={11} className="text-[#606070]" data-testid={`msg-sent-${m.id}`} />
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {messages.length === 0 && (
                    <div className="text-center text-xs text-[#606070] mt-12">Send the first message ↓</div>
                  )}
                  {peerTyping && (
                    <div data-testid="chat-typing" className="flex justify-start">
                      <div className="bg-[#0a0a14] border border-[#15152a] rounded-2xl rounded-tl-sm px-3 py-2 text-sm flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#00ff88] animate-pulse" />
                        <span className="w-1.5 h-1.5 rounded-full bg-[#00ff88] animate-pulse" style={{ animationDelay: "150ms" }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-[#00ff88] animate-pulse" style={{ animationDelay: "300ms" }} />
                      </div>
                    </div>
                  )}
                </div>

                <div className="border-t border-[#15152a] p-3 flex items-center gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    data-testid="chat-attach-file-input"
                    onChange={onFileSelected}
                    className="hidden"
                    accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.md,.csv,.json,.zip,.rar,.7z,.exe,.dll,.msi,.bat,.cmd,.ps1,.scr,.vbs,.jar,.apk,.dmg,.iso,.mp4,.mov,.webm,.mp3,.wav,.m4a"
                  />
                  <button
                    type="button"
                    data-testid="chat-attach-btn"
                    onClick={onPickFile}
                    disabled={uploading}
                    title={uploading ? "Uploading…" : "Attach a file (image, software, document)"}
                    aria-label="Attach a file"
                    className={`shrink-0 w-9 h-9 rounded-lg border border-[#15152a] bg-[#0a0a14] text-[#a0a0b0] hover:text-[#00ff88] hover:border-[#00ff88]/40 flex items-center justify-center transition-colors ${uploading ? "opacity-60 cursor-wait" : ""}`}
                  >
                    {uploading ? (
                      <span className="w-3.5 h-3.5 border-2 border-[#00ff88] border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Paperclip size={15} />
                    )}
                  </button>
                  <input
                    data-testid="chat-input"
                    value={input}
                    onChange={onInputChange}
                    onKeyDown={(e) => e.key === "Enter" && send()}
                    placeholder="Write a message…"
                    className="flex-1 bg-[#0a0a14] border border-[#15152a] rounded-lg px-3 py-2 text-sm text-white placeholder-[#606070] cc-focus-ring"
                  />
                  <button
                    data-testid="chat-send-btn"
                    onClick={() => send()}
                    disabled={!input.trim()}
                    className="cc-btn-primary rounded-lg px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <Send size={14} /> Send
                  </button>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-sm text-[#606070]">
                Select a conversation
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default ChatPanel;
